import React, { useState, useRef } from 'react';
import { uploadImage } from '../../services/contact-form';

const FilesUploadField = ({
    onFilesChange,
    maxFiles = 5,
    acceptedTypes = ['image/*'],
    maxFileSize = 5 * 1024 * 1024,
    className = ''
}) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState([]);
    const fileInputRef = useRef(null);

    const validateFile = (file) => {
        if (file.size > maxFileSize) {
            return `File ${file.name} is too large. Maximum size is ${Math.round(maxFileSize / (1024 * 1024))}MB`;
        }

        const isValidType = acceptedTypes.some(type => {
            if (type === 'image/*') {
                return file.type.startsWith('image/');
            }
            return file.type === type;
        });

        if (!isValidType) {
            return `File ${file.name} is not a supported format`;
        }

        return null;
    };

    const handleFiles = async (newFiles) => {
        try {
            const fileArray = Array.from(newFiles);
            console.log('newFiles', fileArray);
            setUploading(fileArray.map(f => true));
            if (files.length + fileArray.length > maxFiles) {
                alert(`You can only upload up to ${maxFiles} files`);
                return;
            }

            const errors = [];
            const validFiles = [];

            for (const file of fileArray) {
                const error = validateFile(file);
                if (error) {
                    errors.push(error);
                } else {
                    validFiles.push(file);
                }
            }

            if (errors.length > 0) {
                alert(errors.join('\n'));
            }

            if (validFiles.length === 0) return;

            const response = await Promise.all(validFiles.map(file => uploadImage(file)));

            const updatedFiles = [...files, ...response];
            setFiles(updatedFiles);

            if (onFilesChange) {
                onFilesChange(updatedFiles.map(f => f?.id));
            }

        } catch (error) {
            console.error('Error uploading image:', error);
            throw error;
        } finally {
            setUploading([]);
        }

    };

    const removeFile = (fileId) => {
        const updatedFiles = files.filter(f => f?.id !== fileId);
        setFiles(updatedFiles);

        if (onFilesChange) {
            onFilesChange(updatedFiles.map(f => f?.id));
        }
    };

    const handleFileInput = (e) => {

        const selectedFiles = e.target.files;
        handleFiles(selectedFiles);
        e.target.value = '';
    };

    return (
        <div className={`image-uploader ${className}`}>
            {files.length === 0 && !uploading.length && (
                <div
                    onClick={() => fileInputRef.current?.click()}
                    className="upload-area"
                >

                    <div className="upload-label-container">
                        <p className="upload-label"><span>Choose a file</span> <span>or Drag it here</span></p>
                        <p className="upload-label upload-label--mobile"><span>Choose a file</span> <span>or take a photo</span></p>
                    </div>
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={acceptedTypes.join(',')}
                onChange={handleFileInput}
                className="hidden"
            />

            {uploading.map((upload, index) => (
                <div className='image-uploader__preview-grid' key={index}>
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                        <div className="w-full h-full flex items-center justify-center">
                            <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" fill="none">
                                <circle cx="20" cy="20" r="18" stroke="#d64e00" strokeWidth="4" opacity="0.2" />
                                <path d="M38 20a18 18 0 0 1-18 18" stroke="#F26101" strokeWidth="4">
                                    <animateTransform
                                        attributeName="transform"
                                        type="rotate"
                                        from="0 20 20"
                                        to="360 20 20"
                                        dur="1s"
                                        repeatCount="indefinite" />
                                </path>
                            </svg>

                        </div>
                    </div>
                </div>
            ))}
            {files.length > 0 && (
                <div className="w-full">
                    <div className="image-uploader__preview-grid ">
                        {files.map((fileObj) => (
                            <div key={fileObj.id} className="image-uploader__file-container">
                                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                                    <img
                                        src={fileObj.url}
                                        alt={fileObj.id}
                                        className="w-full h-full object-cover"
                                    />

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeFile(fileObj.id);
                                        }}
                                        className="image-uploader__remove-file"
                                    >
                                        <span>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>

                                        </span>
                                    </button>
                                </div>

                            </div>
                        ))}

                        {maxFiles > files.length && !uploading.length && (
                            <div onClick={() => fileInputRef.current?.click()} className=" image-uploader__add-file">
                                +
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilesUploadField;